import { trackPage } from '@/utils/analytics'
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
} from 'vue-router'
import { trackRoute, RouteRoomTag } from '@/utils/route'

const router = createRouter({
  history: createWebHistory(window.ROUTE_PREFIX),
  routes: [
    {
      name: 'main',
      path: '/',
      meta: {
        showHeader: true,
        requiresAuth: true,
        tag: RouteRoomTag.None,
      },
      redirect: () => {
        return window.IN_PLUGIN ? '/p-home' : '/home'
      },
      component: () => import('../views/Main/Main.vue'),
      children: [
        {
          name: 'home',
          path: '/home',
          meta: {
            tag: RouteRoomTag.None,
            showSearch: false,
          },
          component: () => import('../views/Home.vue'),
        },
        {
          name: 'search',
          path: '/search',
          meta: {
            tag: RouteRoomTag.Search,
            prefix: '',
          },
          component: () => import('../views/Search/Search.vue'),
        },
        {
          name: 'project',
          path: '/project/:id',
          meta: {
            tag: RouteRoomTag.Folder,
            showSearch: true,
            prefix: '',
          },
          redirect: (from) => {
            return {
              meta: from.meta,
              params: from.params,
              name: 'folder',
            }
          },
          component: () => import('../views/Project/Project.vue'),
          children: [
            {
              name: 'recycle-bin',
              path: '/project/:id/recycle-bin',
              meta: {
                tag: RouteRoomTag.RecycleBin,
                showSearch: true,
                prefix: '',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/RecycleBin/RecycleBin.vue'),
            },
            {
              name: 'review-links',
              path: '/project/:id/review-links',
              meta: {
                tag: RouteRoomTag.Share,
                showSearch: true,
                prefix: '',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/ReviewLinks/ReviewLinks.vue'),
            },
            {
              name: 'collection-links',
              path: '/project/:id/collection-links',
              meta: {
                tag: RouteRoomTag.Collection,
                showSearch: true,
                prefix: '',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/CollectionLinks/CollectionLinks.vue'),
            },
            {
              name: 'ftp-links',
              path: '/project/:id/ftp-links',
              meta: {
                tag: RouteRoomTag.Collection,
                showSearch: true,
                prefix: '',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/FTPLinks/FTPLinks.vue'),
            },
            {
              name: 'folder',
              path: '/project/:id/:guid?',
              meta: {
                tag: RouteRoomTag.Folder,
                showSearch: true,
                prefix: '',
                keepAlive: false,
              },
              component: () => import('../views/Project/Folder.vue'),
            },
            {
              name: 'folder-not-found',
              meta: {
                tag: RouteRoomTag.None,
                showSearch: true,
              },
              path: '/:pathMatch(.*)*',
              component: () => import('../views/Project/Folder.vue'),
            },
          ],
        },
        {
          name: 'p-project',
          path: '/p-project/:id',
          meta: {
            tag: RouteRoomTag.Folder,
            prefix: 'p-',
            showSearch: true,
          },
          redirect: (from) => {
            return {
              meta: from.meta,
              params: from.params,
              name: 'p-folder',
            }
          },
          component: () => import('../views/Project/Project.vue'),
          children: [
            {
              name: 'p-recycle-bin',
              path: '/p-project/:id/recycle-bin',
              meta: {
                tag: RouteRoomTag.RecycleBin,
                showSearch: true,
                prefix: 'p-',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/RecycleBin/RecycleBin.vue'),
            },
            {
              name: 'p-review-links',
              path: '/p-project/:id/review-links',
              meta: {
                tag: RouteRoomTag.Share,
                showSearch: true,
                prefix: 'p-',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/ReviewLinks/ReviewLinks.vue'),
            },
            {
              name: 'p-collection-links',
              path: '/p-project/:id/collection-links',
              meta: {
                tag: RouteRoomTag.Collection,
                showSearch: true,
                prefix: 'p-',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/CollectionLinks/CollectionLinks.vue'),
            },
            {
              name: 'p-ftp-links',
              path: '/p-project/:id/ftp-links',
              meta: {
                tag: RouteRoomTag.Collection,
                showSearch: true,
                prefix: 'p-',
                keepAlive: false,
              },
              component: () =>
                import('../views/Project/FTPLinks/FTPLinks.vue'),
            },
            
            {
              name: 'p-folder',
              path: '/p-project/:id/:guid?',
              meta: {
                tag: RouteRoomTag.Folder,
                showSearch: true,
                prefix: 'p-',
                keepAlive: false,
              },
              component: () => import('../views/Project/Folder.vue'),
            },
            {
              name: 'folder-not-found',
              meta: {
                tag: RouteRoomTag.None,
                showSearch: true,
              },
              path: '/:pathMatch(.*)*',
              component: () => import('../views/Project/Folder.vue'),
            },
          ],
        },
        {
          name: 'p-search',
          path: '/p-search',
          meta: {
            tag: RouteRoomTag.Search,
            prefix: 'p-',
          },
          component: () => import('../views/Search/Search.vue'),
        },
        {
          name: 'p-home',
          path: '/p-home',
          meta: {
            tag: RouteRoomTag.None,
            showSearch: true,
            prefix: 'p-',
          },
          component: () => import('../views/Home.vue'),
        },
      ],
    },
    {
      name: 'welcome',
      path: '/welcome',
      meta: {
        tag: RouteRoomTag.None,
        requiresAuth: false,
      },
      component: () => import('../views/Login/Login.vue'),
    },
    {
      name: 'signup',
      path: '/signup',
      meta: {
        tag: RouteRoomTag.None,
        requiresAuth: false,
      },
      component: () => import('../views/Signup/Signup.vue'),
    },
    {
      name: 'forgot',
      path: '/forgot',
      meta: {
        tag: RouteRoomTag.None,
        requiresAuth: false,
      },
      component: () => import('../views/Forgot/Forgot.vue'),
    },
    {
      name: 'collection',
      path: '/collection/:id',
      meta: {
        tag: RouteRoomTag.None,
        requiresAuth: false,
      },
      component: () => import('../views/Collection/Collection.vue'),
    },
    {
      name: 'share',
      path: '/share/:id/:guid?',
      meta: {
        tag: RouteRoomTag.None,
        requiresAuth: false,
      },
      component: () => import('../views/Share/Share.vue'),
    },
    {
      name: 'review',
      path: '/review/:id',
      meta: {
        tag: RouteRoomTag.Review,
        requiresAuth: true,
      },
      component: () => import('../views/Review/Review.vue'),
    },
    {
      name: 'editor',
      path: '/editor/:id',
      meta: {
        tag: RouteRoomTag.Editor,
        requiresAuth: true,
      },
      component: () => import('../views/Editor/Editor.vue'),
    },
    {
      name: 'imagine',
      path: '/imagine/:id',
      meta: {
        tag: RouteRoomTag.Imagine,
        requiresAuth: true,
      },
      component: () => import('../views/Imagination/Imagination.vue'),
    },
    {
      name: 'preview',
      path: '/preview/:id',
      meta: {
        tag: RouteRoomTag.Review,
        requiresAuth: false,
      },
      component: () => import('../views/Review/Preview.vue'),
    },
    {
      name: 'not-found',
      path: '/:pathMatch(.*)*',
      meta: {
        tag: RouteRoomTag.None,
        requiresAuth: false,
      },
      redirect: '/home',
      // component: () => import('../views/About.vue'),
    },
  ],
})

router.beforeEach(async (to, from) => {
  trackPage(to.name as string)
  trackRoute(to, from)

  const token = window.REFRESH_TOKEN || localStorage.getItem('token')
  window.REQUIRE_AUTH = to.meta.requiresAuth
  if (to.meta.requiresAuth && !token) {
    // 将用户重定向到登录页面
    return { name: 'welcome' }
  }
})
export default router
